import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

const signupSchema = z.object({
  name: z.string().trim().min(2, "Il nome deve contenere almeno 2 caratteri").max(100, "Il nome è troppo lungo"),
  email: z.string().trim().email("Inserisci un'email valida").max(255, "L'email è troppo lunga"),
  password: z.string().min(6, "La password deve contenere almeno 6 caratteri").max(72, "La password è troppo lunga"),
  privacy: z.boolean().refine((val) => val === true, {
    message: "Devi accettare la Privacy Policy"
  })
});

const Signup = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const form = useForm<z.infer<typeof signupSchema>>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      privacy: false,
    },
  });

  const onSubmit = async (values: z.infer<typeof signupSchema>) => {
    setIsSubmitting(true);
    const { error } = await signUp(values.email, values.password, values.name);
    setIsSubmitting(false);
    
    if (!error) {
      navigate("/profilo");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 font-body antialiased flex items-center justify-center px-4 py-12">
      <Helmet>
        <title>Registrati | Progetto Startup</title>
        <meta name="description" content="Crea il tuo account su Progetto Startup e accedi a corsi, risorse e strumenti per far crescere la tua startup" />
      </Helmet>

      <div className="w-full max-w-md">
        <div className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl p-8">
          <div className="text-center mb-8">
            <a href="/" className="inline-block mb-6 font-display font-semibold tracking-tight text-xl text-slate-100">
              Progetto Startup
            </a>
            <h1 className="text-2xl font-bold text-slate-100 mb-2">Crea il tuo account</h1>
            <p className="text-slate-400 text-sm">Inizia il tuo percorso con Progetto Startup</p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm text-slate-400">Nome completo</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Mario Rossi"
                        {...field}
                        className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#FF6B47] focus-visible:ring-0 focus-visible:ring-offset-0"
                      />
                    </FormControl>
                    <FormMessage className="text-red-400" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm text-slate-400">Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="nome@esempio.com"
                        {...field}
                        className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#FF6B47] focus-visible:ring-0 focus-visible:ring-offset-0"
                      />
                    </FormControl>
                    <FormMessage className="text-red-400" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm text-slate-400">Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          {...field}
                          className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#FF6B47] focus-visible:ring-0 focus-visible:ring-offset-0 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                          aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-400" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="privacy"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="border-white/20 data-[state=checked]:bg-[#FF6B47] data-[state=checked]:border-[#FF6B47]"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-normal text-slate-400">
                        Accetto la{" "}
                        <a href="/privacy-policy" className="text-[#FF6B47] hover:text-[#FF8A6B] transition-colors">
                          Privacy Policy
                        </a>
                      </FormLabel>
                      <FormMessage className="text-red-400" />
                    </div>
                  </FormItem>
                )}
              />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-xl bg-gradient-to-r from-[#FF6B47] to-[#FF8A6B] text-white font-semibold py-2.5 text-sm transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 mt-2"
              >
                {isSubmitting ? "Registrazione..." : "Registrati"}
              </button>
            </form>
          </Form>

          <div className="mt-6 text-center text-sm text-slate-500">
            Hai già un account?{" "}
            <a href="/login" className="text-[#FF6B47] hover:text-[#FF8A6B] font-medium transition-colors">
              Accedi
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;